import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { avatarService } from '@services/avatarService';

interface AvatarContextType {
  avatarUrl: string | null;
  updateAvatar: (newAvatarUrl: string | null) => void;
  refreshAvatar: () => void;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

interface AvatarProviderProps {
  children: ReactNode;
}

export const AvatarProvider: React.FC<AvatarProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Incarca avatar-ul local la initializare
  useEffect(() => {
    if (user?.id) {
      const localAvatar = avatarService.getLocalAvatar(user.id);
      setAvatarUrl(localAvatar);
    } else {
      setAvatarUrl(null);
    }
  }, [user?.id]);

  const updateAvatar = (newAvatarUrl: string | null) => {
    setAvatarUrl(newAvatarUrl);
    
    // Salveaza in localStorage daca este un avatar local
    if (user?.id && newAvatarUrl) {
      avatarService.saveLocalAvatar(user.id, newAvatarUrl);
    } else if (user?.id && !newAvatarUrl) {
      avatarService.removeLocalAvatar(user.id);
    }
  };

  const refreshAvatar = () => {
    if (user?.id) {
      const localAvatar = avatarService.getLocalAvatar(user.id);
      setAvatarUrl(localAvatar);
    }
  };

  const contextValue: AvatarContextType = {
    avatarUrl,
    updateAvatar,
    refreshAvatar,
  };

  return (
    <AvatarContext.Provider value={contextValue}>
      {children}
    </AvatarContext.Provider>
  );
};

export const useAvatar = () => {
  const context = useContext(AvatarContext);
  if (context === undefined) {
    throw new Error('useAvatar must be used within an AvatarProvider');
  }
  return context;
};
